<template>
	<VDialog
		v-model="dialog"
		:fullscreen="fullscreen"
		:persistent="persistent"
		:width="width"
		@click:outside="emitCloseEvent"
		@keydown.escape="emitCloseEvent"
	>
		<div
			:id="uniqueId"
			class="ap-white dialog"
		>
			<div
				class="mb-0 w-100 d-flex align-center flex-nowrap dialog__header"
			>
				<div class="dialog__title">
					<slot name="header">
						<h2
							v-if="title"
							class="ma-0 text-h4"
						>
							{{ title }}
						</h2>
					</slot>
				</div>

				<AmeliproIconBtn
					v-if="!persistent"
					:id="uniqueId ? `${ uniqueId }-close-btn` : undefined"
					btn-label="Fermer la fenêtre modale"
					class="ml-auto dialog__close-btn"
					icon="croix"
					icon-bg-color="transparent"
					icon-color="ap-blue darken-1"
					icon-hover-bg-color="transparent"
					icon-hover-color="ap-blue darken-2"
					size="40px"
					@click="emitCloseEvent"
				/>
			</div>

			<div class="w-100 mt-4 dialog__content">
				<slot />
			</div>

			<div
				v-if="$slots.footer || !noFooter"
				class="w-100 mt-4 dialog__footer"
				:class="$slots.footer ? undefined : 'd-flex flex-column flex-sm-row justify-sm-space-between align-center'"
			>
				<slot name="footer">
					<AmeliproBtn
						v-if="!breakpointHelper.isDesktop()"
						:id="uniqueId ? `${ uniqueId }-validation-btn` : undefined"
						class="mb-4 w-100 dialog__validation-btn--mobile"
						@click="emitConfirmEvent"
					>
						{{ validationBtnLabel }}
					</AmeliproBtn>

					<AmeliproBtn
						:id="uniqueId ? `${ uniqueId }-cancel-btn` : undefined"
						bordered
						class="dialog__cancel-btn"
						:class="breakpointHelper.isDesktop() ? undefined : 'w-100'"
						color="ap-white"
						hover-color="ap-blue lighten-3"
						text-color="ap-blue darken-1"
						@click="emitCloseEvent"
					>
						{{ cancelBtnLabel }}
					</AmeliproBtn>

					<AmeliproBtn
						v-if="breakpointHelper.isDesktop()"
						:id="uniqueId !== undefined ? `${ uniqueId }-validation-btn` : undefined"
						class="dialog__validation-btn"
						@click="emitConfirmEvent"
					>
						{{ validationBtnLabel }}
					</AmeliproBtn>
				</slot>
			</div>
		</div>
	</VDialog>
</template>

<script setup lang="ts">
	import { computed, onUpdated, ref, watch } from 'vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';

	const props = defineProps({
		cancelBtnLabel: {
			type: String,
			default: 'Annuler',
		},
		fullscreen: {
			type: Boolean,
			default: false,
		},
		labelledby: {
			type: String,
			required: true,
		},
		noFooter: {
			type: Boolean,
			default: true,
		},
		persistent: {
			type: Boolean,
			default: false,
		},
		title: {
			type: String,
			default: undefined,
		},
		uniqueId: {
			type: String,
			required: true,
		},
		validationBtnLabel: {
			type: String,
			default: 'Valider',
		},
		value: {
			type: Boolean,
			default: false,
		},
		width: {
			type: String,
			default: '800px',
		},
	});
	const breakpointHelper = new BreakpointHelper('smAndUp');
	const emit = defineEmits(['change', 'confirm', 'input']);

	const dialog = computed({
		get: (): boolean => props.value,
		set: (newValue: boolean): void => {
			emit('input', newValue);
		},
	});

	const emitCloseEvent = (): void => {
		if (!props.persistent) {
			dialog.value = false;
			emit('change', false);
		}
	};

	const emitConfirmEvent = (): void => {
		emit('confirm');
	};

	// set accessibility attributes on the dialog box
	const setDialogAttributes = (): void => {
		const wrapper = document.body.querySelector(`#${props.uniqueId}`)?.parentElement?.parentElement;
		wrapper?.setAttribute('aria-labelledby', props.labelledby);
		getFocusableElements();
	};

	// tabbableEl find all focusable elements in the dialog
	const tabbableEl = ref(document.getElementById(props.uniqueId)?.querySelectorAll('a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'));

	// visibleEl returns visible elements from tabbableEl
	const visibleEl = computed(() => {
		if (tabbableEl.value !== undefined) {
			return Array.from(tabbableEl.value).filter((el: Element) => getComputedStyle(el).getPropertyValue('display') !== 'none' && !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden') && !el.getAttribute('style')?.includes('display: none'));
		}
		return undefined;
	});

	// event on tab for last element of the dialogbox and event on tab+shift for the first element of dialogbox
	const tabEvent = (event: KeyboardEvent, position: string): void => {
		if (event.code === 'Tab' && event.shiftKey && position === 'first') {
			event.preventDefault();
			const element = document.body.querySelector('[data-focusable="last"]') as HTMLElement;
			element?.focus();
		}

		if (event.code === 'Tab' && position === 'last' && !event.shiftKey) {
			event.preventDefault();
			const element = document.body.querySelector('[data-focusable="first"]') as HTMLElement;
			element?.focus();
		}
	};

	// get all visible and focusable elements and set attributes on the first and the last one
	const getFocusableElements = (): void => {
		if (tabbableEl.value) {
			const tabbableElArray = Array.from(tabbableEl.value);
			// reset attributes and events already existing before
			tabbableElArray.filter((el: Element) => el.hasAttribute('data-focusable')).forEach((el: Element) => {
				el.removeEventListener('keydown', (e: Event) => tabEvent(e as KeyboardEvent, 'first'));
				el.removeEventListener('keydown', (e: Event) => tabEvent(e as KeyboardEvent, 'last'));
				el.removeAttribute('data-focusable');
			});

			if (visibleEl.value) {
				const firstFocusable = visibleEl.value[0];
				const lastFocusable = visibleEl.value[visibleEl.value.length - 1];

				// set a data-focusable="first" on the first one and a data-focusable="last"
				firstFocusable.setAttribute('data-focusable', 'first');
				lastFocusable.setAttribute('data-focusable', 'last');

				// set an event on the first and last element to make the focus loop into the dialogbox
				firstFocusable.addEventListener('keydown', (e: Event) => tabEvent(e as KeyboardEvent, 'first'));
				lastFocusable.addEventListener('keydown', (e: Event) => tabEvent(e as KeyboardEvent, 'last'));
			}
		}
	};

	// create an observer to check DOM changes and update focus loop elements
	const observerConfig = { attributes: true, childList: true, subtree: true };
	const observer = new MutationObserver(() => {
		tabbableEl.value = document.getElementById(props.uniqueId)?.querySelectorAll('a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])');
	});
	const openedOnce = ref(false);
	onUpdated(() => {
		if (dialog.value) {
			if (!openedOnce.value) {
				openedOnce.value = true;
				tabbableEl.value = document.getElementById(props.uniqueId)?.querySelectorAll('a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])');
				observer.observe(document.getElementById(props.uniqueId) as Node, observerConfig);
			}
			setDialogAttributes();
		}
	});

	watch(() => breakpointHelper.name, () => {
		// we reset tabbableEl to update visibleEl which will trigger the secund watch
		tabbableEl.value = document.getElementById(props.uniqueId)?.querySelectorAll('a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])');
	});

	watch(() => visibleEl.value, () => {
		// we disconnect the observer and reconnect it after setDialogAttributes to avoid infinite loop
		observer.disconnect();
		setDialogAttributes();
		observer.observe(document.getElementById(props.uniqueId) as Node, observerConfig);
	});
</script>

<style lang="scss" scoped>
	.dialog {
		padding: $card-padding--desktop;

		@media #{$media-down-md} {
			padding: $card-padding--tablet;
		}
	}

	.dialog__header {
		padding-bottom: 0.5rem;
		border-bottom: 1px solid $ap-grey-lighten3;
	}

	.dialog__title {
		width: calc(100% - 60px);
		font-size: $font-size-lg;
	}

	.dialog__content {
		min-height: 150px;
	}
</style>
